import { Button, Modal, Typography } from "antd";
import { useCallback, useState } from "react";
import { Trans, useTranslation } from "next-i18next";
import styled from "styled-components";
import { useAppSelector } from "@/hooks/redux";
import { getDashBoardState } from "@/redux/store";
import Title from "antd/lib/typography/Title";
import Image from "next/image";
import { UpgradeIcon } from "../Icons";
import { poppins } from "@/components/common/fonts";
import { useGetEnvQuery } from "@/redux/api/commonApi";
import { useRouter } from "next/router";
import axios from "axios";
import { useSubscription } from "@/hooks/useSubscription";

const StyledModal = styled(Modal)`
  .ant-modal-content {
    background-image: url(https://cdn-studio.deepbrain.io/asset/dashboard/unlimited_bg.png);
    background-size: cover;
    margin: 0 20px;
  }
  .ant-modal-close {
    color: white;
  }
  h4, p, li {
    color: white;
    font-family: ${poppins.string};
  }
  .centered {
    text-align: center;
  }
`;

const CardWrapper = styled.div`
  font-family: ${poppins.string};
  position: relative;
  margin-top: 20px;
  div {
    color: white;
    font-family: ${poppins.string};
    font-size: 14px;
    position: absolute;
    top: 20px;
    left: 20px;
    .bold {
      font-weight: 700;
    }
  }
  button {
    position: absolute;
    bottom: 20px;
    left: 20px;
    width: 258px;
    height: 43px;
    max-width: 80%;
  }
`

const BodyWrapper = styled.div`
  position: relative;
`


export default function MobileSubscriptionModal({
  modalOpen,
  setModalOpen,
}: {
  modalOpen: boolean,
  setModalOpen: any,
}) {
  const { t } = useTranslation("common");
  const { accountUserInfo } = useAppSelector((store) => getDashBoardState(store) ?? false );
  const router = useRouter();
  const { data: studioUrl } = useGetEnvQuery(
    { key: "NEXTAUTH_URL" },
    {
      pollingInterval: 30 * 60 * 1000,
      refetchOnFocus: false,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: false,
      skip: false,
    }
  );

  const { fetch } = useSubscription();

  const [loading, setLoading] = useState<boolean>(false);

  const handleUpgradeClick = useCallback(async () => {
    try {
      setLoading(true);

      const { plan } = await fetch();

      const apiUrl = "/api/user/subscriptions";
      const uriWithoutQueryParam = router?.asPath.split("?")[0];
      const cancelUrl = `${studioUrl}` + uriWithoutQueryParam;
      const successUrl = `${studioUrl}/checkout_redirect?session_id={CHECKOUT_SESSION_ID}&redirect=${uriWithoutQueryParam}`;
      const couponItem = sessionStorage.getItem("coupon") ?? "";
  
      /**
       * 비로그인 상태인경우 회원가입 페이지로 이동
       */
      if (!accountUserInfo.id) {
        if (confirm(t("Sign up is required. Do you want to go sign up page?"))) {
          router.replace(`/auth/signup?redirect=${encodeURIComponent(cancelUrl)}`);
          return;
        }
      }

      if (!plan?.next || plan?.next === "enterprise") {
        alert(t("For plan upgrades, please contact our customer support."));
        return;
      }

      const periodKey = plan?.interval === "year" ? "yearly" : "monthly";
  
      const subscriptionRes = await axios.post(apiUrl, {
        product: "studio3.0",
        plan: plan?.next || "personal",
        seats: 1,
        periodKey,
        successUrl,
        cancelUrl,
        coupon: couponItem,
      });
  
      if (subscriptionRes.data.success) {
        router?.replace(subscriptionRes.data.data);
      } else {
        Modal.error({ content: subscriptionRes.data.data.msg });
      }
    } catch (e) {
      Modal.error({ content: t("Something went wrong. Please try again later.") });
    } finally {
      setLoading(false);
    }
  }, [accountUserInfo.id, fetch, router, studioUrl, t]);

  return <>
    <StyledModal 
    open={modalOpen} 
    // onOk={handleOk} 
    onCancel={() => setModalOpen(false)}
    footer={null}
    centered>
      <Title level={4} className="centered">
        {"🔥" + t("Enjoy more AI STUDIOS service") + "🔥"}
      </Title>
      <CardWrapper>
        <Image 
          width={0}
          height={0}
          sizes="100vw"
          style={{ width: '100%', height: 'auto', borderRadius: "15px" }}
          src={"https://cdn-studio.deepbrain.io/asset/dashboard/unlimited_card2.png"}
          alt=""
        />
        <Typography.Paragraph>
          <Trans
            i18nKey={
              'Unlimited Videos, <br />Advanced AI Features <br /><bold>All in One Plan!</bold>'
            }
            components={{
              bold: <span className="bold" />,
            }}
          />
        </Typography.Paragraph>
        <Button
          className="upgrade-button"
          onClick={handleUpgradeClick}
          icon={<UpgradeIcon width={16} height={14} fill="white" />}
          style={{
            backgroundColor: "#3075f6",
            border: "1px solid #ffffff",
            color: "#ffffff",
          }}
          loading={loading}
        >
          {t("Upgrade")}
        </Button>
      </CardWrapper>
      <BodyWrapper>
        <Title level={4}>{t("Upgrade today for unlimited creation and creativity!")}</Title>
        <ul>
          <li>{t("Unlimited Videos")} ( {t("up to 30-mins each")} )</li>
          <li>{"70+ " + t("AI Avatars")}</li>
          <li>{t("Unlimited AI Prompts")}</li>
          <li>{t("Image & Video Generator")}</li>
          <li>{t("Script & Text Generator")}</li>
          <li>{t("Premium Stock Images, Videos & Music")}</li>
          <li>{t("Watermark Removed")}</li>
        </ul>
        <Typography.Paragraph style={{color: "white", fontFamily: poppins.string}}>
          {t("* This is the standard for services provided in the Personal plan and above.")}
        </Typography.Paragraph>
      </BodyWrapper>

    </StyledModal>
  </>
}